import _ from 'lodash';
import { takeLatest, call, put, all, select } from 'redux-saga/effects';
import delay from '@redux-saga/delay-p';
import { Experience } from 'common-entities';
import Page from './index.js';

/**
 * does the actual page 'save' using the api
 * @param saveAndClose - close after the save operation
 */
function* _savePages(saveAndClose, pages) {
  try {
    if (pages.length > 0) {
      const [apiResult] = yield all([call(Page.Api.savePages, pages), call(delay, 250)]);
      yield all(_.map(apiResult, (page) => put(Page.Actions.receiveUpdatedPage(page))));
    }
    yield put(Page.Actions.finishSaving());
    yield put(Page.Actions.savePagesSuccess(saveAndClose));

    yield call(reloadEmptyPhases);
  } catch (err) {
    yield put(Page.Actions.savePagesFailure(err));
  }
}

// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
// Update individual page
function* updatePage({ payload: { page } }) {
  const _updatedPage = yield call(Page.Api.updatePage, page);
  if (_updatedPage.error) {
    yield put(Page.Actions.updatePageFailure(page));
  } else {
    yield put(Page.Actions.updatePageSuccess());
    yield call(reloadEmptyPhases);
  }
}

function* savePages({ payload: { unsavedLayouts } }) {
  yield call(_savePages, true, unsavedLayouts);
}

function* applyPages({ payload: { unsavedLayouts } }) {
  yield call(_savePages, false, unsavedLayouts);
}

function* fetchPages({ payload: { experienceId } }) {
  try {
    const pages = yield call(Page.Api.fetchPages, experienceId);
    yield put(Page.Actions.fetchPagesSuccess(pages));
    yield call(reloadEmptyPhases, experienceId);
  } catch (error) {
    yield put(Page.Actions.fetchPagesFailure(error));
  }
}

function* deletePage({ payload: { id } }) {
  const response = yield call(Page.Api.deletePage, id);
  if (response.error) yield put(Page.Actions.deletePageFailure(response.error));
  else {
    yield put(Page.Actions.deletePageSuccess(id));
    yield put(Experience.Actions.loadExperience(null, true));
    yield call(reloadEmptyPhases);
  }
}

function* reloadEmptyPhases(experienceId = '') {
  let eId = experienceId;
  if (!experienceId) {
    const { id } = yield select(Experience.Selectors.experience);
    eId = id;
  }
  const response = yield call(Page.Api.fetchEmptyPhases, eId);

  if (response.error) yield put(Page.Actions.fetchEmptyPhasesFailure(response.error));
  else {
    yield put(Page.Actions.fetchEmptyPhasesSuccess(response));
  }
}

function* handleReloadEmptyPhases() {
  yield call(reloadEmptyPhases);
}

/**
 * Saves the page order to the BE.
 * Initially sets the order into the redux state(so that the DND does not jump around while the api is getting back with a response),
 * then initiates the API save operation. When api comes back with a failure, it resets the redux state to version before the DND update.
 */
function* savePageOrder({ payload: { currentByPhase, reorderedByPhase } }) {
  const experience = yield select(Experience.Selectors.experience);
  const pageById = yield select(Page.Selectors.pagesById);

  const orderedPageIds = Page.Utils.orderedPageIds(reorderedByPhase);
  const orderedPageStates = Page.Utils.orderedPageStates(orderedPageIds, pageById);

  yield put(Page.Actions.setByPhase(reorderedByPhase));

  const response = yield call(Page.Api.reorderStates, experience.id, orderedPageStates.join(','));

  if (response.error) {
    yield put(Page.Actions.setByPhase(currentByPhase)); //resetting before the reorder
    yield put(Page.Actions.savePageOrderFailure(response.error));
  } else {
    yield put(Page.Actions.savePageOrderSuccess());
  }
}

/**
 * As a result of DND, it precalculates the updated order, then initiates the save.
 */
function* reorderPagesInPhase({ payload: { phase, startIndex, endIndex } }) {
  const currentByPhase = yield select(Page.Selectors.byPhase);
  const originalPageOrder = yield select(Page.Selectors.pagesInPhase, phase);

  // work out the new order
  const reorderedPageIds = Array.from(originalPageOrder); // avoid any possible mutation issues by just working with a clean array object
  const [removed] = reorderedPageIds.splice(startIndex, 1);
  reorderedPageIds.splice(endIndex, 0, removed);

  const reorderedByPhase = currentByPhase.set(phase, reorderedPageIds);

  yield call(savePageOrder, { payload: { currentByPhase, reorderedByPhase } }); //initiate the page order save
}

export default function* pageSagas() {
  yield all([
    takeLatest(Page.Actions.updatePage.getType(), updatePage),
    takeLatest(Page.Actions.savePages.getType(), savePages),
    takeLatest(Page.Actions.applyPages.getType(), applyPages),
    takeLatest(Page.Actions.fetchPages.getType(), fetchPages),
    takeLatest(Page.Actions.deletePage.getType(), deletePage),
    takeLatest(Page.Actions.fetchEmptyPhases.getType(), handleReloadEmptyPhases),
    takeLatest(Page.Actions.reorderPagesInPhase.getType(), reorderPagesInPhase),
    takeLatest(Page.Actions.savePageOrder.getType(), savePageOrder),
  ]);
}
